/**
 * An alias's fork and its reset (`SheetAliasFork`): `detach` turns an
 * attached alias's template-expanded children into real, server-backed rows
 * and flips the instance to `hydrated`; `reset` deletes the forked rows and
 * re-attaches the alias to its template. Each persists its rows and the
 * alias's fork flag, and takes itself back as far as the server holds it
 * when a write is refused (`compensate`, `restore`).
 *
 * It owns the fork's rules and its compensation; the rows, the tree, the
 * alias instance store, the template expansion and the row-field writes stay
 * the sheet's, handed in through three narrow roles (`SheetAliasFork.Rows`,
 * `.Expansion`, `.Persist`) the sheet builds over its private members. The
 * template expansion is handed in rather than imported, so the unit names
 * neither `Sheet` nor the alias templates above it, and sits below the sheet.
 */

import { Tracing } from "@tento-core/tracing";
import { Uuid } from "@tento-core/uuid";
import type { Option } from "../core/option.ts";
import type { JsonObject } from "../core/json.ts";
import { elementAt } from "../core/checked-index.ts";
import { RowWriteError } from "../core/errors.ts";
import { RowId } from "../ids/row-id.ts";
import type { SheetId } from "../ids/sheet-id.ts";
import { isSyntheticDefaultRendererId, type RowLocalId } from "../ids/row-local-id.ts";
import type { Backend } from "../backends/rows-backend.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import type { RawRow } from "../wire/raw-sheet-wire.ts";
import type { AddRowSpec, SheetStores } from "./rows-sheet.ts";
import { remapRowIds } from "./sheet-row-codec.ts";
import type { SheetRowFields } from "./sheet-row-fields.ts";

/** Types that go with `SheetAliasFork`: its three host roles. */
export namespace SheetAliasFork {
  /** The sheet's rows and tree. */
  export interface Rows {
    /** The row `id` names, or `null` when it is not this sheet's. */
    row(id: RowLocalId): Option<ModelRow>;
    stores(): Pick<SheetStores, "tree" | "aliasInstance">;
    /** Every descendant of `id`, deepest first. */
    descendants(id: RowLocalId): readonly RowLocalId[];
    /** `row` as its wire row (`snapshotRow` over the sheet's stores). */
    snapshot(row: ModelRow): RawRow;
    /** Add a row to the sheet (`Sheet.addRow`). */
    addRow(spec: AddRowSpec): ModelRow;
    /** Drop `id` and its subtree from the sheet. */
    dropRowSubtree(id: RowLocalId): void;
  }

  /** The alias template expansion the sheet runs. */
  export interface Expansion {
    /** Expand `alias`'s template children that are missing (idempotent). */
    expand(alias: AliasRow): void;
    /** Whether `alias`'s template declares child rows (static, or a dynamic tail). */
    declaresChildren(alias: AliasRow): boolean;
    /** Re-attach `alias` to its template and rebuild its children from it (the reset's re-expansion). */
    rebuild(alias: AliasRow): void;
  }

  /** Where the fork's writes go. */
  export interface Persist {
    readonly backend: Pick<Backend, "addRow" | "deleteRow">;
    /** The sheet the promoted rows are created in. */
    sheetId(): Option<SheetId>;
    /** The sheet's row-field writes: the alias's fork flag enters them. */
    readonly fields: SheetRowFields;
  }
}

/** An alias's fork and reset over a sheet (see the module note). */
export class SheetAliasFork {
  readonly #rows: SheetAliasFork.Rows;
  readonly #expansion: SheetAliasFork.Expansion;
  readonly #persist: SheetAliasFork.Persist;

  constructor(rows: SheetAliasFork.Rows, expansion: SheetAliasFork.Expansion, persist: SheetAliasFork.Persist) {
    this.#rows = rows;
    this.#expansion = expansion;
    this.#persist = persist;
  }

  /**
   * Fork the attached alias `aliasId` (`Sheet.detachAlias`): its owned rows
   * promoted to real rows under fresh ids, the instance detached, both
   * persisted; answers the old → new id remap (empty when the alias is
   * already detached, is not an alias, or its template's children have not
   * materialized). A failure part-way is compensated
   * before it rejects (see `Sheet.detachAlias`).
   */
  async detach(aliasId: RowLocalId): Promise<Map<RowLocalId, RowLocalId>> {
    const remap = new Map<RowLocalId, RowLocalId>();
    const alias = this.#rows.row(aliasId);
    if (!(alias instanceof AliasRow) || alias.instance().hydrated) return remap;
    const stores = this.#rows.stores();

    // A fork MUST be applied to the materialized tree. If we snapshot before the
    // alias's template subtree has expanded — e.g. an auto-fork on a structural
    // edit that races the (async dynamic-tail) expansion — `ownedRows`
    // captures an empty/partial subtree, yet `detach()` below still flips
    // `hydrated:true`. Once hydrated, the template no longer drives expansion and
    // additive reconcile can't re-derive the missing children, so the alias is
    // frozen as a childless shell forever (the calendar-list-renders-empty bug).
    // `expandInstance` is idempotent (re-adds only missing children, emits no
    // NewRowInserted for existing ones), so forcing it here completes the subtree
    // without disturbing an already-materialized one.
    this.#expansion.expand(alias);

    // Snapshot the alias-owned synthetic rows (pre-order: parent
    // before child) before any structural mutation, allocating a
    // fresh real id per row and remapping intra-subtree references.
    const owned = this.#ownedRows(aliasId);
    // A fork does not complete before its template's children materialized.
    // The template declares children but expansion produced none (a dynamic
    // tail whose source has not loaded: the calendar and task lists): flipping
    // `hydrated` now would freeze the alias childless, since a hydrated alias
    // is no longer template-driven. It stays attached, so its children still
    // come when the data does. A template that declares no children forks as
    // before, and an alias the user emptied on purpose is already hydrated.
    if (owned.length === 0 && this.#expansion.declaresChildren(alias)) {
      Tracing.warn("net")("alias-fork/nothing-to-own", { alias: aliasId.rawId, template: alias.descriptor.template });
      return remap;
    }
    const idRemap = new Map<string, string>();
    for (const row of owned) idRemap.set(row.id.rawId, Uuid.make());
    const promoted = owned.map((row) => remapRowIds(this.#rows.snapshot(row), idRemap));

    // Drop the synthetic subtree, flip to detached (so the re-inserted
    // rows are no longer synthetic and a future hydrate won't
    // re-expand), then re-insert them as real rows. `addRow` rejects
    // the `cells` field, so strip it for the local mirror.
    for (const childId of [...stores.tree.children(aliasId)]) {
      this.#rows.dropRowSubtree(childId);
    }
    stores.aliasInstance.detach(aliasId);
    for (const [i, promotedRow] of promoted.entries()) {
      const { cells: _cells, ...spec } = promotedRow;
      remap.set(elementAt(owned, i, "Sheet.detachAlias: owned").id, this.#rows.addRow(spec).id);
    }
    Tracing.log("alias-fork/detach", {
      template: alias.descriptor.template,
      promoted: promoted.length,
    });

    // The alias's fork flag (`hydrated`, its props / overrides) as the wire
    // bag now carries it: painted into every copy of the row at once.
    const fields = this.#persist.fields;
    const flag = fields.beginWireAttributes(aliasId);

    // Persist: the promoted children (pre-order, so a parent is created before
    // its children), then the alias's hydrated flag. Each child carries
    // `parentId`, so the backend links it under its parent. A server-backed
    // sheet mints its own id for each created row: a child's `parentId` (and
    // `attributes.parentRowId`) is mapped through the ids the server answered
    // for the rows already created, since the server knows no client id. Once
    // every row is created, the promoted rows are re-inserted locally under
    // the minted ids (`#adoptMinted`), so the write that follows the fork in
    // the same gesture (the drop's move, a re-parent, an attribute patch)
    // addresses rows the server knows. Ephemeral and in-memory backends
    // answer null and keep the ids as sent.
    const created: RowId[] = [];
    const mintedIds = new Map<string, string>();
    try {
      for (const raw of promoted) {
        const sent = mintedIds.size === 0 ? raw : remapRowIds(raw, mintedIds);
        const minted = await this.#persist.backend.addRow(sent, this.#persist.sheetId(), null, null, null);
        created.push(minted ?? RowId.of(raw.id));
        if (minted != null && minted.toString() !== raw.id) mintedIds.set(raw.id, minted.toString());
      }
    } catch (err) {
      // Never sent: the flag goes back with the fork's compensation.
      fields.ledger.refuse(flag, err, `the fork of ${aliasId.rawId} was not saved`);
      await this.#compensate(aliasId, alias, created);
      throw err;
    }
    if (mintedIds.size > 0) this.#adoptMinted(aliasId, owned, remap, mintedIds);
    try {
      await fields.ledger.run(flag, fields.transport(aliasId), `the fork flag of ${aliasId.rawId} was not saved`);
    } catch (err) {
      // Refused: the ledger took the flag back; the fork is undone too.
      await this.#compensate(aliasId, alias, created);
      throw err;
    }
    return remap;
  }

  /**
   * Put the promoted rows under the ids the server minted for them: the
   * fork's own local step again (drop the alias's promoted subtree, re-insert
   * each row from its snapshot, parents first), with every row id and
   * intra-subtree reference mapped through `mintedIds` (client raw id ->
   * minted raw id). `remap` (synthetic -> promoted local id) is updated to
   * the re-inserted rows, so the fork's answer names rows the server knows.
   * Positions and attributes come from the snapshots, so the order holds.
   */
  #adoptMinted(
    aliasId: RowLocalId,
    owned: readonly ModelRow[],
    remap: Map<RowLocalId, RowLocalId>,
    mintedIds: ReadonlyMap<string, string>,
  ): void {
    const stores = this.#rows.stores();
    // A promoted row's snapshot names it by its wire key (`r_<hex>`); the fork
    // sent the bare client id the key was minted from. Map both spellings.
    const byKey = new Map<string, string>(mintedIds);
    for (const [client, minted] of mintedIds) byKey.set(RowId.of(client).toString(), minted);
    const snapshots: RawRow[] = [];
    for (const synthetic of owned) {
      const local = remap.get(synthetic.id);
      const row = local == null ? null : this.#rows.row(local);
      if (row == null) return; // the fork's rows changed meanwhile: leave them as they are
      snapshots.push(remapRowIds(this.#rows.snapshot(row), byKey));
    }
    for (const childId of [...stores.tree.children(aliasId)]) {
      this.#rows.dropRowSubtree(childId);
    }
    for (const [i, snapshot] of snapshots.entries()) {
      const { cells: _cells, ...spec } = snapshot;
      remap.set(elementAt(owned, i, "SheetAliasFork.#adoptMinted: owned").id, this.#rows.addRow(spec).id);
    }
    Tracing.log("alias-fork/adopt-minted", { rows: snapshots.length });
  }

  /**
   * Take a fork that failed part-way back (`detach`): delete what the
   * backend created (children last-created first), drop the promoted rows,
   * re-attach the alias and re-expand its template. A deletion the backend
   * refuses is logged, never thrown over the fork's own failure.
   */
  async #compensate(aliasId: RowLocalId, alias: AliasRow, created: readonly RowId[]): Promise<void> {
    for (const id of [...created].reverse()) {
      try {
        await this.#persist.backend.deleteRow(id, null);
      } catch (err) {
        Tracing.warn("net")("alias-fork/compensate-failed", {
          row: id.toString(),
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    const stores = this.#rows.stores();
    for (const childId of [...stores.tree.children(aliasId)]) {
      this.#rows.dropRowSubtree(childId);
    }
    stores.aliasInstance.reattach(aliasId);
    this.#expansion.expand(alias);
    Tracing.log("alias-fork/compensated", {
      template: alias.descriptor.template,
      deleted: created.length,
    });
  }

  /**
   * Pre-order list of the rows an alias owns by template expansion —
   * the set `detach` materializes. Nested aliases are leaves
   * (included, not descended into); auto-hydrated default renderers
   * are excluded (re-created on `addRow`).
   */
  #ownedRows(aliasId: RowLocalId): ModelRow[] {
    const out: ModelRow[] = [];
    const tree = this.#rows.stores().tree;
    const visit = (id: RowLocalId): void => {
      for (const childId of tree.children(id)) {
        if (isSyntheticDefaultRendererId(childId)) continue;
        const child = this.#rows.row(childId);
        if (child == null) continue;
        out.push(child);
        if (child instanceof AliasRow) continue;
        visit(childId);
      }
    };
    visit(aliasId);
    return out;
  }

  /**
   * Reset a forked alias back to its template ("reset to main
   * component"). Deletes the promoted real children server-side,
   * clears overrides + flips `hydrated: false`, then re-expands the
   * template from scratch and persists the cleared flag. No-op if the
   * alias is still attached. Destructive: edits made to the forked
   * children are discarded.
   *
   * Detail: row-arch-reference/rowkey-architecture.md Part 9
   * (detached → in-sync is an explicit user action).
   */
  async reset(aliasId: RowLocalId): Promise<void> {
    const alias = this.#rows.row(aliasId);
    if (!(alias instanceof AliasRow) || !alias.instance().hydrated) return;
    const stores = this.#rows.stores();

    // The forked children are real, server-backed rows; their
    // auto-renderer descendants are virtual — skip those. Capture the
    // real RowIds (and each forked row's snapshot, parent first, to put the
    // fork back on a refusal) before any local mutation drops them.
    const realChildIds: RowId[] = [];
    const forked: RawRow[] = [];
    for (const childId of stores.tree.children(aliasId)) {
      const subtree = [childId, ...this.#rows.descendants(childId)];
      for (const id of subtree) {
        const row = this.#rows.row(id);
        if (row != null && row.id.key.isUser()) {
          realChildIds.push(RowId.fromLocalId(id));
          forked.push(this.#rows.snapshot(row));
        }
      }
    }
    const overrides = alias.instance().overrides;

    // Re-attach locally: clear overrides + flip hydrated:false, drop
    // the forked subtree, re-expand from the template.
    this.#expansion.rebuild(alias);
    Tracing.log("alias-fork/reset", {
      template: alias.descriptor.template,
      deleted: realChildIds.length,
    });

    // The alias's cleared fork flag, painted into every copy of the row.
    const fields = this.#persist.fields;
    const flag = fields.beginWireAttributes(aliasId);
    // Persist: delete the promoted rows, then the alias's cleared hydrated
    // flag. A refusal takes the reset back as far as the server holds it: the
    // flag (the ledger), the fork re-marked, and the forked rows the server
    // still holds put back under the alias.
    let deleted = 0;
    try {
      for (const rowId of realChildIds) {
        await this.#persist.backend.deleteRow(rowId, null);
        deleted++;
      }
    } catch (err) {
      fields.ledger.refuse(flag, err, `the reset of ${aliasId.rawId} was not saved`);
      this.#restore(aliasId, overrides, forked.filter((raw) => !realChildIds.slice(0, deleted).some((id) => id.toString() === raw.id)));
      throw RowWriteError.after(
        err,
        "delete",
        "DELETE",
        "/api/rows/{rowKey}",
        `the reset of ${aliasId.rawId} was taken back; ${deleted} of its ${realChildIds.length} forked rows had been deleted on the server`,
      );
    }
    try {
      await fields.ledger.run(flag, fields.transport(aliasId), `the cleared fork flag of ${aliasId.rawId} was not saved`);
    } catch (err) {
      // Every forked row is gone on the server; the alias is still forked
      // there: the sheet shows that — a fork with no children.
      this.#restore(aliasId, overrides, []);
      throw err;
    }
  }

  /**
   * Put a refused reset's fork back (`reset`): the alias detached again
   * with its `overrides`, its re-expanded children dropped, and `rows` (the
   * forked rows the server still holds, parent first) re-added.
   */
  #restore(aliasId: RowLocalId, overrides: JsonObject, rows: readonly RawRow[]): void {
    const stores = this.#rows.stores();
    for (const childId of [...stores.tree.children(aliasId)]) this.#rows.dropRowSubtree(childId);
    stores.aliasInstance.detach(aliasId);
    for (const [attr, value] of Object.entries(overrides)) stores.aliasInstance.setOverride(aliasId, attr, value);
    for (const raw of rows) {
      const { cells: _cells, ...spec } = raw;
      this.#rows.addRow(spec);
    }
    Tracing.log("alias-fork/reset-taken-back", { alias: aliasId.rawId, restored: rows.length });
  }
}
